import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyIcon, MailIcon, ArrowLeftIcon } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import FormSection from '@/components/form/FormSection';
import FormField from '@/components/form/FormField';
import Input from '@/components/form/Input';
import Button from '@/components/form/Button';

export default function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newEmail, setNewEmail] = useState('');

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    try {
      setLoading(true);

      // First verify current password
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: (await supabase.auth.getUser()).data.user?.email || '',
        password: currentPassword
      });

      if (signInError) {
        toast.error('Current password is incorrect');
        return;
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      toast.success('Password updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail) return;

    try {
      setLoading(true);

      // Check if email is already in use
      const { data: existingUser } = await supabase
        .from('auth.users')
        .select('id')
        .eq('email', newEmail)
        .maybeSingle();

      if (existingUser) {
        toast.error('Email is already in use');
        return;
      }

      // Update email
      const { error } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (error) throw error;

      toast.success('Email update confirmation sent to your new email address');
      setNewEmail('');
    } catch (error) {
      console.error('Error updating email:', error);
      toast.error('Failed to update email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Button
        variant="ghost"
        size="sm"
        icon={<ArrowLeftIcon className="h-4 w-4" />}
        onClick={() => navigate('/dashboard')}
        className="mb-6"
      >
        Back to Dashboard
      </Button>

      <h1 className="text-3xl font-bold text-gray-900 mb-8">Account Settings</h1>

      <div className="space-y-6">
        <FormSection title="Change Password">
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <FormField label="Current Password" required>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                icon={<KeyIcon className="h-5 w-5" />}
                required
              />
            </FormField>

            <FormField label="New Password" required>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                icon={<KeyIcon className="h-5 w-5" />}
                required
                minLength={6}
              />
            </FormField>

            <FormField label="Confirm New Password" required>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                icon={<KeyIcon className="h-5 w-5" />}
                required
                minLength={6}
              />
            </FormField>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                loading={loading}
              >
                Update Password
              </Button>
            </div>
          </form>
        </FormSection>

        <FormSection title="Change Email">
          <form onSubmit={handleEmailChange} className="space-y-4">
            <FormField 
              label="New Email Address" 
              required
              helpText="A confirmation email will be sent to your new address"
            >
              <Input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                icon={<MailIcon className="h-5 w-5" />}
                required
              />
            </FormField>

            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={loading || !newEmail}
                loading={loading}
              >
                Update Email
              </Button>
            </div>
          </form>
        </FormSection>
      </div>
    </div>
  );
}